import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Trash2, CheckCircle, Camera, Film, Share2, Move3d } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../hooks/useAuth'
import { modelsApi } from '../../services/api/models'
import { adminApi } from '../../services/api/admin'
import ThumbnailManager from '../../components/admin/ThumbnailManager'
import AudioManager from '../../components/admin/AudioManager'
import { ModelEditorModal } from '../../components/admin/ModelEditorModal'
import { ShowcaseAnimationEditor } from '../../components/admin/ShowcaseAnimationEditor'
import { ShareLinksModal } from '../../components/admin/ShareLinksModal'
import { StaticLayerEditor } from '../../components/admin/StaticLayerEditor'
import { prepareSceneAsset, sceneFileExt } from '../../components/admin/sceneLayerAsset'
import { uploadSceneAsset } from '../../components/admin/sceneLayerUpload'
import { resolveModelSource } from '../../components/ModelViewer'
import type { LayerTransform } from '../../components/ModelViewer/resolveModelSource'
import type { CameraState, ShowcaseAnimation, ModelTransform } from '../../types/camera'

interface ModelFormData {
  name: string
  description: string
  category: string
  tags: string[]
  visibility: 'public' | 'private' | 'unlisted'
  featured: boolean
  xrEnabled?: boolean
  vertices?: number
  duration?: number
}

export default function EditModel() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { token, adminEmail } = useAuth()
  const queryClient = useQueryClient()
  const [tagInput, setTagInput] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [showDefaultCameraModal, setShowDefaultCameraModal] = useState(false)
  const [showAnimationEditor, setShowAnimationEditor] = useState(false)
  const [showShareLinksModal, setShowShareLinksModal] = useState(false)
  const [showStaticLayerEditor, setShowStaticLayerEditor] = useState(false)
  
  const [formData, setFormData] = useState<ModelFormData>({
    name: '',
    description: '',
    category: '',
    tags: [],
    visibility: 'public',
    featured: false
  })
  
  const [defaultCamera, setDefaultCamera] = useState<CameraState | undefined>()
  const [modelTransform, setModelTransform] = useState<ModelTransform | undefined>()
  const [showcaseAnimation, setShowcaseAnimation] = useState<ShowcaseAnimation | undefined>()
  // Pose of the persistent scene 3DGS inside the performer's frame.
  const [staticTransform, setStaticTransform] = useState<LayerTransform | undefined>()
  // R2 key of the scene layer. Seeded from metadata and updated in place when a
  // new file is attached, so the Align button enables without a page reload.
  const [staticPath, setStaticPath] = useState<string | undefined>()
  const [sceneUploading, setSceneUploading] = useState(false)
  const [sceneError, setSceneError] = useState<string | null>(null)
  const [sceneStatus, setSceneStatus] = useState<string | null>(null)

  const { data: modelData, isLoading: modelLoading } = useQuery({
    queryKey: ['admin-model', id],
    queryFn: () => adminApi.getModel(id!),
    enabled: !!id
  })

  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => modelsApi.getCategories()
  })

  useEffect(() => {
    const loadData = async () => {
      if (modelData?.data) {
        const model = modelData.data
        setFormData({
          name: model.name,
          description: model.description || '',
          category: model.category,
          tags: Array.isArray(model.tags) ? model.tags : (typeof model.tags === 'string' ? JSON.parse(model.tags) : []),
          visibility: model.visibility,
          featured: model.featured,
          xrEnabled: model.xrEnabled || false,
          vertices: model.vertices,
          duration: model.duration
        })
        
        // Load camera settings from metadata
        if (model.metadata) {
          const metadata = typeof model.metadata === 'string' ? JSON.parse(model.metadata) : model.metadata
          
          // Migrate camera data if needed
          if (metadata.defaultCamera) {
            const { migrateCameraState } = await import('@/utils/cameraConversion')
            setDefaultCamera(migrateCameraState(metadata.defaultCamera))
          }
          
          setModelTransform(metadata.modelTransform)
          setStaticTransform(metadata.staticLayer?.transform)
          setStaticPath(metadata.sequence?.staticPath)
          
          // Migrate animation keyframes if needed
          if (metadata.showcaseAnimation) {
            const { migrateCameraState } = await import('@/utils/cameraConversion')
            const migratedAnimation = {
              ...metadata.showcaseAnimation,
              keyframes: metadata.showcaseAnimation.keyframes?.map((kf: any) => ({
                ...kf,
                camera: migrateCameraState(kf.camera)
              })) || []
            }
            setShowcaseAnimation(migratedAnimation)
          }
        }
      }
    }
    
    loadData()
  }, [modelData])

  // Attach a scene 3DGS to an already-uploaded model, using multipart for large
  // assets. The uploadId is recovered from the model's
  // file_path, which for a sequence is models/{uploadId}/manifest.json.
  const handleSceneUpload = async (file: File) => {
    const fp = modelData?.data?.filePath || ''
    const uploadId = /^models\/([^/]+)\//.exec(fp)?.[1]
    if (!uploadId) {
      setSceneError('Could not determine the upload id from this model\u2019s file path')
      return
    }
    if (!sceneFileExt(file.name)) {
      setSceneError('Scene layer must be a .spz or .ply file')
      return
    }
    setSceneUploading(true)
    setSceneError(null)
    try {
      // .spz uploads verbatim; .ply is transcoded to SH degree 0 .spz first.
      const prepared = await prepareSceneAsset(file, setSceneStatus)
      const key = await uploadSceneAsset(prepared, {
        apiBase: import.meta.env.VITE_API_URL,
        uploadId, token, adminEmail,
        onStatus: setSceneStatus,
      })
      setStaticPath(key)
      // Identity is the right default: a scene solved from the same COLMAP
      // sparse/0 as the frames already lines up.
      if (!staticTransform) {
        setStaticTransform({
          position: { x: 0, y: 0, z: 0 },
          quaternion: { x: 0, y: 0, z: 0, w: 1 },
          scale: 1,
        })
      }
    } catch (e) {
      setSceneError(e instanceof Error ? e.message : 'Scene upload failed')
    } finally {
      setSceneUploading(false)
      setSceneStatus(null)
    }
  }

  const updateModelMutation = useMutation({
    mutationFn: async (data: ModelFormData) => {
      // Prepare metadata with camera settings.
      // The API hands metadata back as a JSON STRING, so parse it ONCE and use
      // the parsed object everywhere below. Reading `.sequence` off the raw
      // value would yield undefined and silently replace the whole sequence
      // block (frameIds, framesPrefix, fps, …) — which makes the model
      // unrenderable, since PUT /:id replaces metadata wholesale.
      const rawMetadata = modelData?.data?.metadata || {}
      const currentMetadata: Record<string, any> =
        typeof rawMetadata === 'string' ? JSON.parse(rawMetadata) : (rawMetadata as Record<string, any>)
      const metadata = {
        ...currentMetadata,
        defaultCamera,
        modelTransform,
        showcaseAnimation,
        // Only write staticLayer once a pose has been authored, so models
        // without a scene keep their metadata unchanged.
        ...(staticTransform ? { staticLayer: { transform: staticTransform } } : {}),
        // Merge into the existing sequence block, never replace it.
        ...(staticPath
          ? { sequence: { ...(currentMetadata.sequence ?? {}), staticPath } }
          : {}),
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        },
        body: JSON.stringify({
          ...data,
          featured: Boolean(data.featured), // Ensure featured is a boolean
          xrEnabled: Boolean(data.xrEnabled), // Ensure xrEnabled is a boolean
          vertices: data.vertices || undefined,
          duration: data.duration || undefined,
          metadata: metadata // Send as object, not string
        })
      })
      if (!response.ok) {
        const error = await response.json()
        console.error('Update error:', error)
        if (error.details) {
          console.error('Validation details:', error.details)
        }
        throw new Error(error.error || error.message || 'Failed to update model')
      }
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model', id] })
      // Show success message
      setShowSuccess(true)
      setTimeout(() => setShowSuccess(false), 3000)
    }
  })

  const deleteModelMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/v2/admin/models/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Email': adminEmail!
        }
      })
      if (!response.ok) throw new Error('Failed to delete model')
      return response.json()
    },
    onSuccess: () => {
      navigate('/admin/models')
    }
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (sceneUploading) return
    await updateModelMutation.mutateAsync(formData)
  }

  const handleDelete = async () => {
    if (confirm('Are you sure you want to delete this model? This action cannot be undone.')) {
      await deleteModelMutation.mutateAsync()
    }
  }

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }))
      setTagInput('')
    }
  }

  const removeTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }))
  }

  const categories = categoriesData?.data || []

  // Routing (single / ply / sequence / clipgstream) + URL derivation all live
  // in resolveModelSource; the editor modals pick their viewer from `source`.
  const source = useMemo(
    () => (modelData?.data ? resolveModelSource(modelData.data) : null),
    [modelData?.data?.id, modelData?.data?.filePath, modelData?.data?.metadata],
  )
  const sequenceInfo = source?.sequenceInfo ?? null
  const splatAudioUrl = source?.audioUrl ?? null
  const isPlyModel = source?.kind === 'sequence' || source?.kind === 'ply'

  if (modelLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Loading model...</p>
      </div>
    )
  }

  if (!modelData?.data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Model not found</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/admin/models')}
                className="mr-4 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Edit Model</h1>
            </div>
            <button
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 flex items-center gap-2"
            >
              <Trash2 className="w-5 h-5" />
              Delete Model
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Model Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Model Information</h2>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
              <div>
                <span className="font-medium">File Size:</span> {(modelData.data.fileSize / (1024 * 1024)).toFixed(2)} MB
              </div>
              <div>
                <span className="font-medium">Views:</span> {modelData.data.viewCount}
              </div>
              <div>
                <span className="font-medium">Downloads:</span> {modelData.data.downloadCount}
              </div>
              <div>
                <span className="font-medium">Created:</span> {new Date(modelData.data.createdAt).toLocaleDateString()}
              </div>
            </div>

            <div className="border-t pt-4">
              <ThumbnailManager
                modelId={id!}
                currentThumbnail={modelData.data.thumbnailPath}
                previewVideoPath={modelData.data.previewVideoPath}
                onUpdate={() => {
                  queryClient.invalidateQueries({ queryKey: ['model', id] })
                }}
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <AudioManager
                modelId={id!}
                audioUrl={sequenceInfo?.audioUrl ?? splatAudioUrl}
              />
            </div>
          </div>

          {/* Model Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Model Details</h2>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  id="category"
                  required
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select a category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add a tag"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-indigo-100 text-indigo-800"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-2 text-indigo-600 hover:text-indigo-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="vertices" className="block text-sm font-medium text-gray-700 mb-1">
                    Vertices (optional)
                  </label>
                  <input
                    id="vertices"
                    type="number"
                    value={formData.vertices || ''}
                    onChange={(e) => setFormData({ ...formData, vertices: e.target.value ? parseInt(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                    Duration (seconds)
                  </label>
                  <input
                    id="duration"
                    type="number"
                    step="any"
                    min="0"
                    value={formData.duration || ''}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value ? parseFloat(e.target.value) : undefined })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Visibility Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Visibility Settings</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visibility
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="public"
                      checked={formData.visibility === 'public'}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value as any })}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Public</span> - Anyone can view and download
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="unlisted"
                      checked={formData.visibility === 'unlisted'}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value as any })}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Unlisted</span> - Only accessible with direct link
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="private"
                      checked={formData.visibility === 'private'}
                      onChange={(e) => setFormData({ ...formData, visibility: e.target.value as any })}
                      className="mr-2"
                    />
                    <span className="text-sm">
                      <span className="font-medium">Private</span> - Only specific users can access
                    </span>
                  </label>
                </div>
                
                {/* Share Links Button */}
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => setShowShareLinksModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    <Share2 size={16} />
                    Manage Share Links
                  </button>
                </div>
              </div>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">
                  <span className="font-medium">Featured</span> - Display prominently in gallery
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.xrEnabled || false}
                  onChange={(e) => setFormData({ ...formData, xrEnabled: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm">
                  <span className="font-medium">XR/AR Enabled</span> - Allow viewing in VR/AR mode
                </span>
              </label>
            </div>
          </div>

          {/* Camera Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Camera Settings</h2>
            
            <div className="space-y-4">
              {/* Default Camera */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Default Camera</h3>
                  <p className="text-sm text-gray-500">
                    Set the initial viewing angle when users open this model
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDefaultCameraModal(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
                >
                  <Camera size={16} />
                  Edit Camera
                </button>
              </div>

              {/* Showcase Animation */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Showcase Animation</h3>
                  <p className="text-sm text-gray-500">
                    Create a camera animation for homepage showcase
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAnimationEditor(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2"
                >
                  <Film size={16} />
                  {showcaseAnimation ? 'Edit' : 'Create'} Animation
                </button>
              </div>

              {/* Scene Layer — only meaningful for viewers that render one. */}
              {(source?.kind === 'sequence' || source?.kind === 'ply') && (
                <div className="flex items-center justify-between">
                  <div className="min-w-0 pr-4">
                    <h3 className="text-sm font-medium text-gray-700">Scene Layer</h3>
                    <p className="text-sm text-gray-500">
                      {staticPath
                        ? 'A static scene renders behind the sequence. Align it against the performer.'
                        : 'Attach a scene 3DGS (.spz or .ply) that stays put while the sequence plays.'}
                    </p>
                    {sceneError && <p className="text-sm text-red-600 mt-1">{sceneError}</p>}
                    {sceneStatus && !sceneError && (
                      <p role="status" className="text-sm text-indigo-600 mt-1">{sceneStatus}</p>
                    )}
                    {staticPath && !sceneError && (
                      <p className="text-xs text-gray-400 mt-1 truncate">{staticPath}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <label className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 cursor-pointer border border-gray-300">
                      <input
                        type="file"
                        accept=".spz,.ply"
                        className="hidden"
                        disabled={sceneUploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0]
                          e.target.value = ''
                          if (f) void handleSceneUpload(f)
                        }}
                      />
                      {sceneUploading ? (sceneStatus?.startsWith('Converting') ? 'Converting…' : 'Uploading…')
                        : staticPath ? 'Replace' : 'Attach Scene'}
                    </label>
                    <button
                      type="button"
                      disabled={!staticPath || sceneUploading}
                      onClick={() => setShowStaticLayerEditor(true)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Move3d size={16} />
                      Align Scene
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/models')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateModelMutation.isPending || sceneUploading}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-5 h-5" />
              {updateModelMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Success Notification */}
        {showSuccess && (
          <div className="fixed bottom-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
            <CheckCircle size={20} />
            Model updated successfully
          </div>
        )}

        {/* Model Editor Modal */}
        {showDefaultCameraModal && modelData?.data && (
          <ModelEditorModal
            modelId={id!}
            modelUrl={source?.modelUrl ?? ''}
            isPlyModel={isPlyModel}
            sequenceInfo={sequenceInfo}
            audioUrl={splatAudioUrl}
            source={source}
            currentCamera={defaultCamera}
            currentTransform={modelTransform}
            onSave={(camera, transform) => {
              setDefaultCamera(camera)
              setModelTransform(transform)
              setShowDefaultCameraModal(false)
            }}
            onCancel={() => setShowDefaultCameraModal(false)}
          />
        )}

        {/* Scene Layer Alignment Modal */}
        {showStaticLayerEditor && source && (
          <StaticLayerEditor
            // Override from local state so a scene just attached in this session
            // previews immediately, before the model row has been saved.
            source={{
              ...source,
              staticUrl: staticPath
                ? `${import.meta.env.VITE_R2_PUBLIC_URL || 'https://4dgs-models.moonshine-studio.net'}/${staticPath}`
                : source.staticUrl,
              staticTransform: staticTransform ?? source.staticTransform,
            }}
            currentTransform={staticTransform ?? null}
            onSave={(t) => {
              setStaticTransform(t)
              setShowStaticLayerEditor(false)
            }}
            onCancel={() => setShowStaticLayerEditor(false)}
          />
        )}

        {/* Showcase Animation Editor Modal */}
        {showAnimationEditor && modelData?.data && (
          <ShowcaseAnimationEditor
            modelId={id!}
            modelUrl={source?.modelUrl ?? ''}
            filePath={modelData.data.filePath}
            isPlyModel={isPlyModel}
            sequenceInfo={sequenceInfo}
            audioUrl={splatAudioUrl}
            source={source}
            currentAnimation={showcaseAnimation}
            defaultCamera={defaultCamera}
            onSave={(animation) => {
              setShowcaseAnimation(animation)
              setShowAnimationEditor(false)
            }}
            onCancel={() => setShowAnimationEditor(false)}
          />
        )}

        {/* Share Links Modal */}
        {showShareLinksModal && modelData?.data && (
          <ShareLinksModal
            modelId={id!}
            modelName={modelData.data.name}
            onClose={() => setShowShareLinksModal(false)}
          />
        )}
      </main>
    </div>
  )
}
